class StarkLiveSearch extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-stark-live-results]');
    if (!this.form || !this.input || !this.results) return;

    this.timer = null;
    this.controller = null;
    this.abortEvents = new AbortController();
    this.resultsHome = this.results.parentNode;
    this.resultsNextSibling = this.results.nextSibling;

    this.input.addEventListener('input', () => this.queueSearch(), { signal: this.abortEvents.signal });
    this.input.addEventListener('keydown', (event) => this.handleKeyboard(event), {
      signal: this.abortEvents.signal,
    });
    this.form.addEventListener(
      'submit',
      (event) => {
        if (!this.input.value.trim()) event.preventDefault();
      },
      { signal: this.abortEvents.signal }
    );

    document.addEventListener(
      'pointerdown',
      (event) => {
        const target = event.target;
        if (!(target instanceof Node)) return;
        if (!this.contains(target) && !this.results.contains(target) && !this.closest('.stark-mobile-search-panel')) {
          this.close();
        }
      },
      { signal: this.abortEvents.signal }
    );

    window.addEventListener('resize', () => this.positionDesktopResults(), { signal: this.abortEvents.signal });
    window.addEventListener('scroll', () => this.positionDesktopResults(), {
      signal: this.abortEvents.signal,
      capture: true,
      passive: true,
    });
  }

  disconnectedCallback() {
    this.abortEvents?.abort();
    this.controller?.abort();
    window.clearTimeout(this.timer);
    this.restoreResults();
  }

  queueSearch() {
    window.clearTimeout(this.timer);
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.close();
      return;
    }
    this.timer = window.setTimeout(() => this.search(query), 180);
  }

  async search(query) {
    this.controller?.abort();
    this.controller = new AbortController();
    this.renderState('Buscando…');

    const root = window.Shopify?.routes?.root || '/';
    const parameters = new URLSearchParams({
      q: query,
      'resources[type]': 'product,collection',
      'resources[limit]': '8',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      section_id: 'stark-predictive-search',
    });

    try {
      const response = await fetch(`${root}search/suggest?${parameters}`, {
        headers: { Accept: 'text/html' },
        signal: this.controller.signal,
      });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

      const text = await response.text();
      if (this.input.value.trim() !== query) return;

      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const section = parsed.querySelector('#shopify-section-stark-predictive-search');
      if (!section) throw new Error('Predictive search section was not returned.');

      this.results.innerHTML = section.innerHTML;
      this.open();
    } catch (error) {
      if (error.name !== 'AbortError') this.renderState('No pudimos cargar las sugerencias.');
    }
  }

  renderState(message) {
    const state = document.createElement('p');
    state.className = 'stark-live-search-results__state';
    state.textContent = message;
    this.results.replaceChildren(state);
    this.open();
  }

  handleKeyboard(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.results.hidden) return;
    const links = [...this.results.querySelectorAll('a')];
    if (!links.length) return;

    event.preventDefault();
    const current = links.indexOf(document.activeElement);
    const next =
      event.key === 'ArrowDown'
        ? links[(current + 1 + links.length) % links.length]
        : links[(current - 1 + links.length) % links.length];
    next.focus();
  }

  portalResults() {
    if (!matchMedia('(min-width: 991px)').matches) return;
    if (this.results.parentNode !== document.body) document.body.append(this.results);
    this.results.classList.add('stark-live-search-results--portal');
    this.positionDesktopResults();
  }

  positionDesktopResults() {
    if (this.results.hidden || !this.results.classList.contains('stark-live-search-results--portal')) return;
    const rect = this.form.getBoundingClientRect();
    const viewportPadding = 12;
    const availableHeight = Math.max(220, window.innerHeight - rect.bottom - viewportPadding);

    this.results.style.left = `${Math.round(rect.left)}px`;
    this.results.style.top = `${Math.round(rect.bottom + 6)}px`;
    this.results.style.width = `${Math.round(rect.width)}px`;
    this.results.style.maxHeight = `${Math.min(520, availableHeight)}px`;
  }

  restoreResults() {
    if (!this.results || !this.resultsHome || this.results.parentNode === this.resultsHome) return;
    if (this.resultsNextSibling && this.resultsNextSibling.parentNode === this.resultsHome) {
      this.resultsHome.insertBefore(this.results, this.resultsNextSibling);
    } else {
      this.resultsHome.append(this.results);
    }
    this.results.classList.remove('stark-live-search-results--portal');
    this.results.removeAttribute('style');
  }

  reset() {
    this.controller?.abort();
    window.clearTimeout(this.timer);
    this.input.value = '';
    this.results.replaceChildren();
    this.close();
  }

  open() {
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.portalResults();
  }

  close() {
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.restoreResults();
  }
}

if (!customElements.get('stark-live-search')) {
  customElements.define('stark-live-search', StarkLiveSearch);
}

function installStarkSearchStyles() {
  document.querySelector('#stark-search-overrides')?.remove();

  const style = document.createElement('style');
  style.id = 'stark-search-overrides';
  style.textContent = `
    #header-group,
    .stark-header-section,
    .stark-header,
    .stark-header__primary,
    .stark-header__search,
    .stark-header__search form {
      overflow: visible !important;
    }

    #header-group,
    .stark-header-section {
      position: relative !important;
      z-index: 1000 !important;
    }

    .stark-header {
      position: relative !important;
      z-index: 5000 !important;
    }

    .stark-nav {
      position: relative !important;
      z-index: 10 !important;
    }

    .stark-header__search {
      position: relative !important;
      z-index: 5100 !important;
    }

    .stark-live-search-results {
      overscroll-behavior: contain;
    }

    .stark-live-search-results--portal {
      position: fixed !important;
      right: auto !important;
      bottom: auto !important;
      z-index: 2147483000 !important;
      margin: 0 !important;
      padding: 8px !important;
      overflow-y: auto !important;
      border: 1px solid #dedede !important;
      border-top: 3px solid #ff9900 !important;
      border-radius: 0 0 3px 3px !important;
      background: #fff !important;
      color: #242424 !important;
      box-shadow: 0 22px 55px rgb(0 0 0 / 28%) !important;
    }

    .stark-live-search-results__list {
      display: grid;
      gap: 2px;
    }

    .stark-live-search-results__item {
      display: grid !important;
      grid-template-columns: 64px minmax(0, 1fr) auto !important;
      gap: 12px !important;
      align-items: center !important;
      min-height: 76px;
      padding: 8px !important;
      border-bottom: 1px solid #ededed;
    }

    .stark-live-search-results__media,
    .stark-live-search-results__media img,
    .stark-live-search-results__image {
      display: grid;
      width: 64px !important;
      height: 64px !important;
      place-items: center;
      object-fit: contain;
      background: #f4f5f6;
    }

    .stark-live-search-results__copy {
      display: grid;
      min-width: 0;
      gap: 4px;
    }

    .stark-live-search-results__title {
      display: block;
      min-width: 0;
      color: #242424;
      font-size: 13px;
      font-weight: 700;
      line-height: 1.3;
    }

    .stark-live-search-results__title mark {
      padding: 0;
      background: transparent;
      color: inherit;
      font-weight: 850;
    }

    .stark-live-search-results__vendor {
      color: #777;
      font-size: 11px;
    }

    .stark-live-search-results__meta {
      display: grid;
      justify-items: end;
      gap: 3px;
      min-width: 94px;
      text-align: right;
    }

    .stark-live-search-results__price {
      display: block;
      color: #111;
      font-size: 14px !important;
      font-weight: 850 !important;
      white-space: nowrap;
    }

    .stark-live-search-results__compare-price {
      color: #888;
      font-size: 11px;
      text-decoration: line-through;
      white-space: nowrap;
    }

    .stark-live-search-results__sold-out {
      padding: 3px 6px;
      border: 1px solid #d9d9d9;
      color: #666;
      font-size: 9px;
      font-weight: 800;
      letter-spacing: .08em;
      text-transform: uppercase;
    }

    .stark-live-search-results__collection span:last-child {
      color: #777;
      font-size: 11px;
      font-weight: 600;
    }

    .stark-live-search-results__empty {
      margin: 0;
      padding: 28px 14px;
      color: #666;
      font-size: 13px;
      text-align: center;
    }

    @media (min-width: 991px) {
      .stark-header__actions .stark-header__mobile-search {
        display: none !important;
      }
    }

    .stark-mobile-search-panel[hidden] {
      display: none !important;
    }

    .stark-mobile-search-panel {
      position: fixed;
      inset: 0;
      z-index: 2147483000;
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100dvh;
      background: #fff;
      color: #242424;
    }

    body.stark-mobile-search-open {
      overflow: hidden !important;
    }

    body.stark-mobile-search-open #search-modal {
      display: none !important;
    }

    .stark-mobile-search-panel__top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      min-height: 58px;
      padding: max(10px, env(safe-area-inset-top)) 16px 10px;
      background: #000;
      color: #fff;
    }

    .stark-mobile-search-panel__top strong {
      font-size: 16px;
      letter-spacing: -.01em;
    }

    .stark-mobile-search-panel__close {
      display: grid;
      width: 40px;
      height: 40px;
      place-items: center;
      padding: 0;
      border: 0;
      background: transparent;
      color: #fff;
      font-size: 28px;
      line-height: 1;
      cursor: pointer;
    }

    .stark-mobile-search-panel stark-live-search,
    .stark-mobile-search-panel form {
      display: flex;
      flex: 1;
      min-height: 0;
      flex-direction: column;
    }

    .stark-mobile-search-panel__bar {
      display: flex;
      flex: 0 0 auto;
      min-height: 54px;
      margin: 0;
      padding: 12px 14px;
      background: #000;
    }

    .stark-mobile-search-panel__bar input {
      min-width: 0;
      flex: 1;
      padding: 0 14px;
      border: 0;
      border-radius: 0;
      background: #fff;
      color: #242424;
      outline: 0;
      font-size: 16px;
    }

    .stark-mobile-search-panel__bar button {
      display: grid;
      width: 54px;
      flex: 0 0 54px;
      place-items: center;
      padding: 0;
      border: 0;
      background: #ff9900;
      color: #000;
    }

    .stark-mobile-search-panel__bar svg {
      width: 23px;
      height: 23px;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.8;
    }

    .stark-mobile-search-panel .stark-live-search-results {
      position: static !important;
      inset: auto !important;
      flex: 1;
      min-height: 0;
      max-height: none !important;
      padding: 10px 12px calc(24px + env(safe-area-inset-bottom)) !important;
      overflow-y: auto;
      border: 0 !important;
      border-radius: 0 !important;
      background: #fff;
      box-shadow: none !important;
    }

    .stark-mobile-search-panel .stark-live-search-results__all {
      position: sticky;
      bottom: 0;
      margin-top: 12px;
      padding: 14px;
      background: #000;
      color: #fff;
    }

    @media (max-width: 990px) {
      .stark-mobile-search-panel .stark-live-search-results__item {
        grid-template-columns: 58px minmax(0, 1fr) !important;
        min-height: 72px;
      }

      .stark-mobile-search-panel .stark-live-search-results__media,
      .stark-mobile-search-panel .stark-live-search-results__media img,
      .stark-mobile-search-panel .stark-live-search-results__image {
        width: 58px !important;
        height: 58px !important;
      }

      .stark-mobile-search-panel .stark-live-search-results__meta {
        grid-column: 2;
        justify-items: start;
        min-width: 0;
        text-align: left;
      }
    }
  `;
  document.head.append(style);
}

function closeNativeSearchModal() {
  const nativeDialog = document.querySelector('#search-modal dialog');
  if (nativeDialog instanceof HTMLDialogElement && nativeDialog.open) nativeDialog.close();
}

function createStarkMobileSearchPanel() {
  let panel = document.querySelector('#stark-mobile-search-panel');
  if (panel) return panel;

  const root = window.Shopify?.routes?.root || '/';
  panel = document.createElement('aside');
  panel.id = 'stark-mobile-search-panel';
  panel.className = 'stark-mobile-search-panel';
  panel.hidden = true;
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-label', 'Buscar productos');
  panel.innerHTML = `
    <div class="stark-mobile-search-panel__top">
      <strong>Buscar productos</strong>
      <button class="stark-mobile-search-panel__close" type="button" aria-label="Cerrar búsqueda">×</button>
    </div>
    <stark-live-search>
      <form action="${root}search" method="get" role="search">
        <div class="stark-mobile-search-panel__bar">
          <label class="visually-hidden" for="stark-mobile-search-input">Buscar productos</label>
          <input
            id="stark-mobile-search-input"
            name="q"
            type="search"
            placeholder="¿Qué estás buscando?"
            autocomplete="off"
            aria-autocomplete="list"
            aria-controls="stark-mobile-live-search-results"
            aria-expanded="false"
          >
          <input name="options[prefix]" type="hidden" value="last">
          <button type="submit" aria-label="Buscar">
            <svg aria-hidden="true" viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></svg>
          </button>
        </div>
        <div
          id="stark-mobile-live-search-results"
          class="stark-live-search-results"
          role="listbox"
          data-stark-live-results
          hidden
        ></div>
      </form>
    </stark-live-search>
  `;

  document.body.append(panel);
  panel.querySelector('.stark-mobile-search-panel__close')?.addEventListener('click', closeStarkMobileSearch);
  return panel;
}

let previousSearchTrigger = null;

function openStarkMobileSearch(trigger) {
  if (!matchMedia('(max-width: 990px)').matches) return;

  closeNativeSearchModal();
  const panel = createStarkMobileSearchPanel();
  previousSearchTrigger = trigger instanceof HTMLElement ? trigger : null;
  panel.hidden = false;
  document.body.classList.add('stark-mobile-search-open');

  requestAnimationFrame(() => {
    closeNativeSearchModal();
    panel.querySelector('input[type="search"]')?.focus();
  });
}

function closeStarkMobileSearch() {
  const panel = document.querySelector('#stark-mobile-search-panel');
  if (!panel) return;

  panel.querySelector('stark-live-search')?.reset?.();
  panel.hidden = true;
  document.body.classList.remove('stark-mobile-search-open');
  closeNativeSearchModal();
  previousSearchTrigger?.focus();
  previousSearchTrigger = null;
}

installStarkSearchStyles();

document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest(
      '.stark-header__mobile-search, .stark-mobile-dock button[aria-label="Buscar productos"]'
    );
    if (!trigger || !matchMedia('(max-width: 990px)').matches) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    openStarkMobileSearch(trigger);
  },
  true
);

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && document.body.classList.contains('stark-mobile-search-open')) {
    event.preventDefault();
    closeStarkMobileSearch();
  }
});
